import { useState, useEffect } from "react";
import { useAppSelector } from "../../../store";
import { storedLocale } from "../../Authentication/util";
import { KPI_ID } from "../../../services/AboEnrichment/AboEnrichmentAPI.types";
import { useGetABOEnrichmentDataMutation } from "../../../services/AboEnrichment/AboEnrichmentAPI";

const PV_TYPE_MAP: Record<string, string> = {
  "001": "ppv",
  "925": "vcspv",
  "007": "pcpv",
};

interface UsePpvRequirementTypeReturn {
  ppvRequirementType: string;
  ppvTitleKey: string;
  isLoading: boolean;
}

/**
 * Custom hook to fetch and determine the PPV requirement type for the current user.
 * Returns the requirement type code and the corresponding translation key.
 *
 * @param bonusPeriod - The bonus period to fetch data for
 * @returns Object containing ppvRequirementType, ppvTitleKey, and loading state
 */
export const usePpvRequirementType = (bonusPeriod: string): UsePpvRequirementTypeReturn => {
  const { user } = useAppSelector((state) => state.boot);
  const [ppvRequirementType, setPpvRequirementType] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const [fetchAboEnrichment] = useGetABOEnrichmentDataMutation();

  useEffect(() => {
    if (!ppvRequirementType) {
      setIsLoading(true);
      fetchAboEnrichment({
        affAbos: [`${user.aff}-${user.abo}`],
        bonusPeriod,
        isoCountryCode: user.isoCountryCode,
        locale: storedLocale(),
        kpis: [KPI_ID.PERSONAL_PV_REQ],
      })
        .unwrap()
        .then((result) => {
          const ppvReq = result?.aboKpiData?.[0]?.personalPVRequirement;
          if (ppvReq) {
            setPpvRequirementType(ppvReq.volume?.code ?? "");
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [ppvRequirementType, fetchAboEnrichment, user.aff, user.abo, bonusPeriod, user.isoCountryCode]);

  const ppvTitleKey = PV_TYPE_MAP[ppvRequirementType] ?? "ppv";

  return {
    ppvRequirementType,
    ppvTitleKey,
    isLoading,
  };
};
